<template>
  <div class="px-3">
    <div class="d-flex align-items-center">
      <h3 class="title mb-1 py-3">Hours and payments report - {{ currentProjectName }}</h3>
      <div class="ml-3 table-control d-flex align-items-center">
          <b-form-checkbox
            size="sm"
            switch
            v-model="includeLoggedLastMonth"
            class="switch"
          >Include logged hours from last month</b-form-checkbox>
      </div>
      <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          size="sm"
          switch
          v-model="includePaidLastMonth"
          class="switch"
        >Include paid hours from last month</b-form-checkbox>
      </div>
      <div class="ml-3 table-control d-flex align-items-center">
        <b-form-checkbox
          size="sm"
          switch
          v-model="showCommercialHours"
          class="switch"
        >Show commercial hours</b-form-checkbox>
      </div>
    </div>

    <div class="table-container" :class="{'bg-white': !isDataLoading && getData.length}">
      <div class="d-flex justify-content-between align-items-center bg-white">
        <div class="d-flex align-items-center mb-4" v-if="!isDataLoading && getData.length">
          <UsersStateListComponent
            class="mr-4"
            :table-name="uiTableName"
            :members-list="getMembers"
            @changeActive="changeActive($event)" />
          <div class="d-flex align-items-center table-text">
            <div
              class="commission ml-2"
              @click="changeStateCommission()"
            >
              Commission: <span v-if="showCommission">show</span><span v-else>hide</span>
            </div>
          </div>
        </div>

        <div
          v-if="projectsList.length && !isDataLoading"
          class="d-flex mb-4"
        >
          <multiselect
            class="multiselect-projects"
            v-model="projectData"
            :options="projectsList"
            :placeholder="'Select project'"
            :track-by="'name'"
            :label="'name'"
            :allow-empty="false"
            :showLabels="false"
            @select="getProjectPage"
          />
        </div>
      </div>

      <div ref="tableContain">
        <b-table-simple
          v-if="!isDataLoading && getData.length"
          class="table__theme-default slideout__table position-relative"
          id="my-table"
          :fixed="false"
          hover
          stripped
          responsive
        >
          <colgroup><col></colgroup>
          <template v-for="member in getMembers">
            <colgroup class="group-border" :key="`${member.position}-${member.name}`" v-if="userState[member.id]"><col v-for="n in columnsCountForUser"></colgroup>
          </template>
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>
          <colgroup><col></colgroup>

          <b-thead head-variant="light">
            <b-tr class="thead-row row-top" :class="{'sticky-head': getCoordinates > 0}" :style="{'top': getCoordinates + 'px'}">
              <b-th class="text-center" :style="{'min-width': thsWidth[`thmonth`] + 'px'}" sticky-column>month</b-th>
              <template v-for="(member, i) in getMembers" variant="secondary">
                <b-th class="sticky-border" :key="`${member.name}-${i}`" variant="secondary" :colspan="columnsCountForUser"  :style="{'min-width': thsWidth[`userth${member.id}`] + 'px'}" v-if="userState[member.id]">
                  <div>
                    <TableUserItem class="text-center m-auto" :user="member" :item-max-width="'180px'" />
                  </div>
                  <div class="sticky-head__second-line d-flex justify-content-between align-items-center text-center mt-2">
                    <span>logged hours</span>
                    <span v-if="showCommercialHours">commercial hours</span>
                    <span>paid hours</span>
                  </div>
                </b-th>
              </template>
              <b-th class="text-center text-lowercase" :style="{'min-width': thsWidth[`thcomission`] + 'px'}" v-if="showCommission">Commission $</b-th>
              <b-th class="text-center text-lowercase" :style="{'min-width': thsWidth[`thamount`] + 'px'}">Amount $</b-th>
              <b-th class="text-center text-lowercase" :style="{'min-width': thsWidth[`thlinks`] + 'px'}">Invoices links</b-th>
            </b-tr>
            <b-tr>
              <b-th sticky-column><div :ref="`thmonth`" /></b-th>
              <template v-for="(member, i) in getMembers" variant="secondary">
                <b-th class="member-name" :key="`${member.name}-${i}`" variant="secondary" :colspan="columnsCountForUser" v-if="userState[member.id]">
                  <div :ref="`userth${member.id}`">
                    <TableUserItem class="text-center m-auto" :user="member" :item-max-width="'180px'" />
                  </div>
                </b-th>
              </template>
              <b-th v-if="showCommission"><div :ref="`thcomission`" /></b-th>
              <b-th><div :ref="`thamount`" /></b-th>
              <b-th><div :ref="`thlinks`" /></b-th>
            </b-tr>
            <b-tr>
              <b-th  class="month-group text-center text-lowercase" sticky-column>Month</b-th>
              <template v-for="member in getMembers" variant="secondary">
                <b-th class="text-center text-lowercase" :key="`${member.name}-hl`" v-if="userState[member.id]">Logged hours </b-th>
                <b-th class="text-center text-lowercase" :key="`${member.name}-hc`" v-if="userState[member.id] && showCommercialHours">Commercial hours </b-th>
                <b-th class="text-center text-lowercase" :key="`${member.name}-hp`" v-if="userState[member.id]">Paid hours </b-th>
              </template>
              <b-th v-if="showCommission" class="text-center text-lowercase">Commission $</b-th>
              <b-th class="text-center text-lowercase">Amount $</b-th>
              <b-th class="text-center text-lowercase">Invoices links</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr class="table-row" v-for="(data) in getData" :key="data.month">
              <b-td class="text-center" sticky-column>{{ data.month }}</b-td>
              <template v-for="member in data.members">
                <b-td class="text-center" :key="`${member.name}-hl-${data.month}`" v-if="userState[member.id]">{{ member.hours_logged || '-' }}</b-td>
                <b-td class="text-center variable-td cursor-pointer"
                      :key="`${member.name}-hc-${data.month}`"
                      v-if="userState[member.id] && showCommercialHours"
                      :variant="member.hours_commercial != member.hours_logged && +member.hours_commercial !== 0 ? 'danger' : ''"
                      @dblclick="showChange(data.month, member.id, member.hours_commercial)"
                >
                  <span class="val" v-if="!tdChange[`change-${data.month}-${member.id}`]">{{ member.hours_commercial || '0' }}</span>
                  <div
                    v-else
                    class="d-flex justify-content-around align-items-center"
                  >
                    <span class="material-icons cancel" @click="hideChange(data.month, member.id)">close</span>
                    <b-form-input
                      class="input-change"
                      type="number"
                      v-model="member.hours_commercial"
                      number
                      maxlength="5"
                      autofocus
                      @keyup.enter="saveChange(member.id, data.month, member.hours_commercial)"
                      @keyup.esc="hideChange(data.month, member.id)"
                    />
                    <span class="material-icons done" @click="saveChange(member.id, data.month, member.hours_commercial)">done</span>
                  </div>
                </b-td>
                <b-td class="text-center" :key="`${member.name}-hp-${data.month}`" v-if="userState[member.id]">
                  <div v-if="member.hours_not_paid"
                      class="not-paid"
                      v-b-tooltip.html="{ variant: 'base' }"
                      :title="member.hours_paid + ' paid hours, ' + member.hours_not_paid + ' not paid hours'"
                  >{{ (member.hours_paid + member.hours_not_paid) | round(2)}}</div>
                  <div v-else>{{ member.hours_paid || '-' }}</div>
                </b-td>
              </template>
              <b-td v-if="showCommission" class="text-center">
                <div v-if="data.commission_not_paid"
                     class="not-paid"
                     v-b-tooltip.html="{ variant: 'base' }"
                     :title="data.commission + ' paid commissions, ' + data.commission_not_paid + ' not paid commissions'"
                >{{ (data.commission + data.commission_not_paid) | round(2)}}</div>
                <div v-else>{{ data.commission || '0' }}</div>
              </b-td>
              <b-td class="text-center">
                <div v-if="data.amount_not_paid"
                    class="not-paid"
                    v-b-tooltip.html="{ variant: 'base' }"
                    :title="data.amount + ' paid, ' + data.amount_not_paid + ' not paid'"
                >{{ getAmount(data) | round(2) }}</div>
                <div v-else>{{ getAmount(data) | round(2) }}</div>
              </b-td>
              <b-td>
                <InvoicesLinksItems
                  v-if="data.invoices"
                  :invoice-key="data.month"
                  :invoices-data="data.invoices"
                  :quantity-show-invoices="2"
                  :templates="getTemplates"
                  @reloadInvoices="loadHoursAndPaymentsPageData"
                />
              </b-td>
            </b-tr>
          </b-tbody>
          <b-tfoot class="tfoot">
            <b-tr>
              <b-td sticky-column class="text-lowercase">Total</b-td>
              <template v-for="(totalData, i) in getTotalData">
                <b-td class="text-center" :key="`${i}-hl`" v-if="userState[totalData.id]">{{ totalData.total_hl || '0' | round(2) }}</b-td>
                <b-td class="text-center" :key="`${i}-hc`" v-if="userState[totalData.id] && showCommercialHours">{{ totalData.total_hc || '0' | round(2) }}</b-td>
                <b-td class="text-center" :key="`${i}-hp`" v-if="userState[totalData.id]">{{ totalData.total_hp || '0' | round(2) }}</b-td>
              </template>
              <b-td v-if="showCommission"></b-td>
              <b-td class="text-center">{{ totalAmount | round(2) }}</b-td>
              <b-td></b-td>
            </b-tr>
            <b-tr>
              <b-td sticky-column class="text-lowercase">Prepaid h</b-td>
              <template v-for="(totalData, i) in getTotalData">
                <b-td class="text-center" :variant="totalData.prepaid < 0 ? 'danger' : 'success'" :key="i" :colspan="columnsCountForUser" v-if="userState[totalData.id]">{{ totalData.prepaid | round(2) }}</b-td>
              </template>
            </b-tr>
            <b-tr>
              <b-td sticky-column></b-td>
              <b-td class="text-center" :colspan="allActiveUsers ? allActiveUsers * columnsCountForUser : 1" :variant="totalPrepaid < 0 ? 'danger' : 'success'">{{ totalPrepaid | round(2) }}</b-td>
              <b-td></b-td>
              <b-td>
                <b-button
                    variant="success"
                    v-if="$can(permissions.CreateFinancesInvoices)"
                    type="button"
                    class="button"
                    size="sm"
                    @click.prevent="showCreateInvoices"
                  >
                    Create invoice
                  </b-button>
                </b-td>
            </b-tr>
          </b-tfoot>
        </b-table-simple>
        <div class="mx-3"  v-if="!isDataLoading && getData.length !== 0 && currentProject.id">
          <div class="col">
            <div class="editor-container">
              <TextEditor
                :entity-id="currentProject.id"
                :entity-type="commentEntityTypes.ReportsHoursAndPayments"
                :in-modal="false"
                :show-text="true"
                data-already-loaded
              />
            </div>
          </div>
        </div>
      </div>
      <div class="preloader" v-if="isDataLoading">
        <div class="spinner-border spinner-theme_default text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <div v-if="!isDataLoading && getData.length === 0">
        No data for this project
      </div>
    </div>

    <FinanceInvoicesModal
      v-model="createInvoices.show"
      ref="invoiceModalCreate"
      mode="create"
      :data="createInvoices.form"
      :title="createInvoices.title"
      :bankDetails="getOwnBankDetails"
      :clientBankDetails="getClientsBankDetailsAll"
      :templates="getTemplates"
      :projects="getActualProjects"
      :statuses="getStatuses"
      :types="getInvoicesTypes"
      :isLoading="getIsModalLoading"
      @submit="createdFinanceInvoices"
    />
  </div>
</template>

<script>
  import Moment from 'moment'
  import {cloneDeep} from 'lodash'
  import {mapActions, mapGetters} from 'vuex'
  import {commentEntityTypes} from '@mocks/comment-entity-types'
  import {permissions} from '@mocks/permissions'
  import StickyHeaderMixin from '@/mixins/StickyHeaderMixin'
  import TableUserItem from '@components/tables/TableUserItem'
  import FinanceInvoicesModal from '@components/modals/FinanceInvoicesModal'
  import UsersStateListComponent from '@components/UsersStateListComponent'
  import InvoiceFormModal from '../../mixins/InvoiceFormModal'
  import InvoicesLinksItems from '@components/InvoicesLinksItems'
  import TextEditor from '@components/TextEditor'

  export default {
  name: 'TableWithDetails',
  mixins: [ InvoiceFormModal, StickyHeaderMixin ],
  components: {
    TableUserItem,
    FinanceInvoicesModal,
    UsersStateListComponent,
    InvoicesLinksItems,
    TextEditor
  },
  props: {
    currentProject: {
      type: Object,
      default: () => null
    },
    projectsList: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      permissions,
      isDataLoading: false,
      invoicesLoading: false,
      showCommission: false,
      includeLoggedLastMonth: false,
      includePaidLastMonth: false,
      showCommercialHours: false,
      showCommercialColumnKey: 'commercial_hours',
      rawData: [],
      userState: {},
      allActiveUsers: 0,
      quantityShowInvoices: 2,
      tdChange: {},
      tempValue: null,
      projectData: null,
      refreshCommercialHours: true
    }
  },
  mounted () {
    this.fetchComments({ type: commentEntityTypes.ReportsHoursAndPayments })
    this.loadHoursAndPaymentsPageData()
    this.loadTableHiddenColumns(this.uiTableName)
      .then(() => {
        this.showCommercialHours = this.getTableHiddenColumnKeys(this.uiTableName).includes(this.showCommercialColumnKey)
      })
    this.fetchInvoiceFormData()
  },
  watch: {
    currentProject: {
      deep: true,
      handler (v) {
        this.projectData = v
      }
    },
    projectsList: {
      deep: true,
      handler (v) {
        this.projectsList = v
      }
    },
    showCommercialHours (value) {
      if (value) {
        this.addHiddenColumnKey({ tableName: this.uiTableName, key: this.showCommercialColumnKey })
      } else {
        this.removeHiddenColumnKey({ tableName: this.uiTableName, key: this.showCommercialColumnKey })
      }
    }
  },
  computed: {
    ...mapGetters({
      getTableHiddenColumnKeys: 'frontView/getTableHiddenColumnKeys'
    }),
    currentProjectName () {
      return this.currentProject.name ?? this.currentProject.key
    },
    uiTableName () {
      return 'hours_and_payments_report_' + this.currentProject.key
    },
    columnsCountForUser () {
      return this.showCommercialHours ? 3 : 2
    },
    getMembers () {
      const data = cloneDeep(this.rawData)
      const members = Object.entries(data)
        .map(([, monthData]) => {
          delete monthData.summary
          return monthData
        })
      let uniqueMembers = members
        .reduce(function (uniqueMembers, monthMembersData) {
          for (const memberName in monthMembersData) {
            if (!uniqueMembers.hasOwnProperty(memberName)) {
              uniqueMembers[memberName] = monthMembersData[memberName]
            }
            uniqueMembers[memberName]['user']['jira_user_name'] = memberName
          }
          return uniqueMembers
        }, [])

      uniqueMembers = Object.entries(uniqueMembers)
        .map(([, user]) => {
          return user.user
        })
        .sort((a, b) => a.name === 'By project' || a.name > b.name ? 1 : -1)

      return uniqueMembers
    },
    getData () {
      const data = Object.entries(this.rawData).map(([monthYM, monthData]) => {
        const item = {
          month: monthYM,
          commission: monthData.summary.commission,
          commission_not_paid: monthData.summary.commission_not_paid,
          amount: monthData.summary.amount,
          amount_not_paid: monthData.summary.amount_not_paid,
          invoices: monthData.summary.invoices,
          members: []
        }
        const members = this.getMembers
        for (const i in members) {
          const memberName = members[i].name
          const memberJiraUserName = members[i].jira_user_name

          let hoursLogged = 0
          let hoursPaid = 0
          let hoursNotPaid = 0
          let hoursCommercial = 0

          if (monthData[memberJiraUserName]) {
            hoursLogged = monthData[memberJiraUserName].summary.loggedHours
            hoursPaid = monthData[memberJiraUserName].summary.paidHours
            hoursNotPaid = monthData[memberJiraUserName].summary.notPaidHours
            hoursCommercial = monthData[memberJiraUserName].summary.commercialHours
          }

          if (monthYM === Moment().format('YYYY-MM')) {
            if (!this.includeLoggedLastMonth) {
              hoursLogged = null
            }
            if (!this.includePaidLastMonth) {
              hoursPaid = null
              hoursNotPaid = null
            }
          }

          item.members.push({
            id: members[i].id,
            name: memberName,
            position: members[i].position,
            employment_type: members[i].employment_type,
            payment_type: members[i].payment_type,
            hours_logged: hoursLogged,
            hours_paid: hoursPaid,
            hours_commercial: hoursCommercial,
            hours_not_paid: hoursNotPaid
          })
        }
        return item
      })

      return data
    },
    getTotalData () {
      this.refreshCommercialHours
      const total = this.getData.reduce(function (total, monthItem) {
        for (const id in monthItem.members) {
          if (total[id] === undefined) {
            total[id] = {
              id: monthItem.members[id].id,
              total_hl: 0,
              total_hc: 0,
              total_hp: 0,
              prepaid: 0
            }
          }
          total[id].total_hl += monthItem.members[id].hours_logged
          total[id].total_hc += monthItem.members[id].hours_commercial
          total[id].total_hp += monthItem.members[id].hours_paid + monthItem.members[id].hours_not_paid
          total[id].prepaid = total[id].total_hp - total[id].total_hl
        }

        return total
      }, [])
      return total
    },
    totalPrepaid () {
      return this.getSum(this.getTotalData, 'prepaid')
    },
    totalAmount () {
      return this.getSum(this.getData, 'amount')
    },
    commentEntityTypes () {
      return commentEntityTypes
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments',
      loadTableHiddenColumns: 'frontView/loadTableHiddenColumns',
      addHiddenColumnKey: 'frontView/addHiddenColumnKey',
      removeHiddenColumnKey: 'frontView/removeHiddenColumnKey'
    }),
    getSum (arr, key) {
      let sum = 0
      for (let i in arr) {
        sum += arr[i][key]
      }
      return sum
    },
    getAmount (data) {
      return data.amount + data.amount_not_paid
    },
    showCreateInvoices () {
      if (this.currentProject.id) {
        this.createInvoices.show = true
        this.$refs['invoiceModalCreate'].resetForm()
        const formFields = cloneDeep(this.$refs['invoiceModalCreate'].form.fields)
        const invoiceData = {}
        Object.keys(formFields).forEach(function (key) {
          invoiceData[key] = formFields[key].value
        })
        invoiceData.project = cloneDeep(this.projectData)
        this.createInvoices.form = invoiceData
        this.$refs['invoiceModalCreate'].changeProject(cloneDeep(this.projectData))
      } else {
        this.$snotify.error(
          'Please create project ' + this.currentProjectName + ' before creating invoice',
          'Error',
          this.$store.state.snotify
        )
      }
    },
    hideInvoices () {
      this.createInvoices.show = false
    },
    createdFinanceInvoices (fields) {
      this.createInvoice(fields)
        .then(() => {
          this.hideInvoices()
          this.loadHoursAndPaymentsPageData()
        })
    },
    loadHoursAndPaymentsPageData () {
      this.fetchHoursAndPaymentsReport()
        .then(() => {
          this.invoicesLoading = true

          this.fetchInvoices()
            .then(() => {
              this.invoicesLoading = false
            })
        })
    },
    fetchHoursAndPaymentsReport () {
      return new Promise((resolve, reject) => {
        this.isDataLoading = true
        let params = {
          projects: [this.currentProjectName]
        }
        return this.$api.finances
          .getReportsHoursAndPayments(params)
          .then(response => {
            if (response.status === 200) {
              this.rawData = response.data
            }
            resolve(true)
          })
          .catch(error => {
            const errors = Object.values(error.response.data.errors)
            for (var i = 0; i < errors.length; i++) {
              this.$snotify.error(
                errors[i][0],
                'Error',
                this.$store.state.snotify
              )
            }
            reject(error)
          })
          .finally(() => {
            this.isDataLoading = false
          })
      })
    },
    changeStateCommission () {
      this.showCommission = !this.showCommission
      this.getThWidth()
    },
    fetchInvoices () {
      const params = '?page=1&filter[project_id]=' + this.currentProject.id
      return this.$api.finances.getInvoices(params)
    },
    changeActive (disUsers) {
      const members = this.getMembers
      for (const i in members) {
        if (disUsers.some(id => members[i].id === id)) {
          this.userState[members[i].id] = false
        } else {
          this.userState[members[i].id] = true
        }
      }
      this.allActiveUsers = Object.values(this.userState).filter(item => !!item).length
      this.getThWidth()
      this.$forceUpdate()
    },
    showChange (month, memberId, val = 0) {
      this.tdChange = {}
      this.tdChange[`change-${month}-${memberId}`] = true
      this.tempValue = val

      this.$forceUpdate()
    },
    hideChange (month, memberId) {
      const curMonth = this.getData.find(data => data.month === month)
      const curMember = curMonth.members.find(member => member.id === memberId)

      curMember.hours_commercial = this.tempValue

      this.tdChange = {}
      this.tempValue = null
      this.$forceUpdate()
    },
    saveChange (userId, dateYM, hours) {
      this.tdChange = {}
      this.refreshCommercialHours++

      this.axios
        .post('/api/finances/commercial-hours/save-to-hp-report', {
          project_id: this.currentProject.id,
          user_id: userId,
          date_ym: dateYM,
          hours
        }, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then(({ data }) => {
          this.$snotify.success(
            'Commercial hours successfully saved',
            'Success',
            this.$store.state.snotify
          )
          this.$forceUpdate()
        })
        .catch(error => {
          const errors = Object.values(error.response.data.errors)
          for (var i = 0; i < errors.length; i++) {
            this.$snotify.error(
              errors[i][0],
              'Error',
              this.$store.state.snotify
            )
          }
        })
    },
    getProjectPage (data) {
      this.$router.push({ name: 'h-p-report', params: { id: data.id } })
    }
  }
}
</script>

<style lang="scss" scoped>
  .table-container {
    padding: 10px;
    border-radius: 10px;

    .preloader {
      background-color: transparent;
    }

    .table-row {
      height: 30px;
      td {
        padding-top: 0px;
        padding-bottom: 0px;
      }
    }

    .table-text {
      font-size: 12px;
      font-weight: 500;
      color: #7489A9;
    }
  }
  .slideout__table {
    &.table__theme-default {
      .table {
        background-color: #fff;
        thead {
          border-bottom: 1px solid #dee2e6;
        }
        tr {
          th {
            text-transform: none;
            background-color: #fff;
          }
        }
      }
    }
    .group-border {
      border-left: 1px solid #dee2e6;
      border-right: 1px solid #dee2e6;
    }
    .tfoot {
      font-weight: bold;
      border-top: 1px solid #E4E7EE;
    }
  }
  .member-name {
    max-width: 200px;
    min-width: 200px;

    ::v-deep.user-name {
      max-width: 140px;
    }
    ::v-deep.user-position {
      max-width: 120px;
    }
  }
  .month-group {
    min-width: 70px;
  }
  .commission {
    font-size: 16px;
    cursor: pointer;
    color: #3D5170;
    .text-on {
      color: #3FA52F;
    }
    .text-off {
      color: #DC3545;
    }
  }
  .thead-row {
    display: none;
    &.sticky-head {
      min-height: 70px;
      padding: 0;
      display: table-row;
      align-items: center;
      background-color: #fff;
      position: absolute;
      left: 0;
      right: 0;
      z-index: 9;

      .sticky-head__second-line {
        span {
          padding: 6px 5px 10px;

          &:first-child {
            padding: 6px 10px 10px 0;
          }
          &:last-child {
            padding: 6px 0px 10px 10px;
          }
        }
      }
    }
  }
  .invoice-badge {
    font-size: 14px;
    padding: 5px 12px;
    background-color: #DEEBFF;
  }
  .not-paid{
    background-color: #ede742;
  }
  .invoice-more {
    height: 100%;
    color: #007bff;
    cursor: pointer;
  }
  .invoice-td {
    display: flex;
    align-items: center;
    .b-toast {
      position: absolute;
    }
  }
  .variable-td {
    min-width: 100px;
    .val {
      font-weight: bold;
      text-decoration: underline;
    }

    input.form-control {
      width: 40px;
      height: 25px;
      padding: 4px;
      min-height: 25px;
      text-align: center;
    }
  }
  .material-icons {
    &.done {
      color: #3FA52F;
    }
    &.cancel {
      color: #DC3545;
    }
  }
  .multiselect-projects {
    width: 200px;
  }
  .editor-container {
    background-color: #fff;
    border-radius: 10px;
    padding: 15px;
  }
</style>
